import React from 'react';
import { View, Text, ScrollView } from 'react-native';
import i18n from 'i18n-js';
import { font as initialFont } from '@styles/fonts';
import { ColorFunction } from '@styles/colors/types';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { isWeb } from '@helpers';
import { CellRenderer } from '@components/settings/components/common/animatedTable/types';

interface WidthConversionParams {
  width: string | number;
  containerWidth: number;
  scale: ScaleFunction;
}

/**
 * Convert percentage width to pixels for mobile platforms
 * For web, percentages work natively so we return as-is
 */
const convertWidth = ({ width, containerWidth, scale }: WidthConversionParams): string | number => {
  if (isWeb) {
    return width;
  }

  if (typeof width === 'string' && width.includes('%') && containerWidth > 0) {
    const percentage = parseFloat(width) / 100;
    // Subtract padding from container width (paddingLeft + paddingRight = 15 + 30 = 45)
    const availableWidth = containerWidth - scale(45);
    return availableWidth * percentage;
  }

  return width;
};

export interface ColumnCellProps<T = any> {
  column: any;
  item: T;
  configIndex: number;
  containerWidth: number;
  C: ColorFunction;
  font: typeof initialFont;
  scale: ScaleFunction;
  cellRenderer?: CellRenderer<T>;
  valuesAlreadyTranslated: boolean;
}

/**
 * Memoized column cell component for AnimatedTable
 * Renders a single column cell with proper memoization to prevent unnecessary re-renders
 */
const ColumnCell = <T = any>({
  column: c,
  item,
  configIndex,
  containerWidth,
  C,
  font,
  scale,
  cellRenderer,
  valuesAlreadyTranslated,
}: ColumnCellProps<T>): React.ReactElement => {
  // Check if this is a metadata-based column (has 'type' field and cellRenderer provided)
  const isMetadataColumn = c.type && cellRenderer;

  let cellContent: React.ReactElement | null = null;

  if (isMetadataColumn) {
    // New metadata-based rendering
    cellContent = cellRenderer!(c, item, {
      C,
      font,
      scale,
      valuesAlreadyTranslated,
    });
  } else {
    // Legacy function-based rendering
    cellContent = (
      <>
        <Text
          numberOfLines={1}
          style={[
            {
              color: c?.getDynamicColor ? c?.getDynamicColor(item) : c.color,
              fontSize: scale(17),
            },
            font[c.valueFont || 'Body'],
          ]}
        >
          {c.key ? (valuesAlreadyTranslated ? item[c.key] : i18n.t(item[c.key])) : c.function(item)}
        </Text>

        {c.bottom && (
          <Text style={[{ color: c.bottom.color, fontSize: scale(13), ...c.bottom.style }, font['Button 2']]}>
            {i18n.t(item[c.bottom.key])}
          </Text>
        )}
      </>
    );
  }

  // Convert width at render time (CRITICAL: keeps config object stable)
  const convertedWidth = convertWidth({ width: c.width, containerWidth, scale });

  return (
    <View
      key={`config2-${c.type || c.key}-${configIndex}`}
      style={[{ width: convertedWidth, justifyContent: 'center', ...c.style }, c.scrollable && isWeb && { height: '100%' }]}
    >
      {c.scrollable && isWeb ? (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ alignItems: 'center', height: '100%' }}
          style={{ width: '100%' }}
        >
          {cellContent}
        </ScrollView>
      ) : (
        cellContent
      )}
    </View>
  );
};

// Memoize the component with a custom comparison function
// Only re-render if actual props change (not just references)
export default React.memo(ColumnCell, (prevProps, nextProps) => {
  return (
    prevProps.item === nextProps.item &&
    prevProps.column === nextProps.column &&
    prevProps.containerWidth === nextProps.containerWidth &&
    prevProps.configIndex === nextProps.configIndex &&
    prevProps.cellRenderer === nextProps.cellRenderer &&
    prevProps.valuesAlreadyTranslated === nextProps.valuesAlreadyTranslated &&
    prevProps.C.scheme === nextProps.C.scheme
  );
}) as <T = any>(props: ColumnCellProps<T>) => React.ReactElement;
